export type TokenScope = {
  orgId: string | undefined;
  wsId: string | undefined;
  flowFolderIds: string[] | undefined;
  roles: string[] | undefined;
};

export const decodeTokenScope = (
  token: string | null | undefined,
): TokenScope => {
  if (!token) {
    return {
      orgId: undefined,
      wsId: undefined,
      flowFolderIds: undefined,
      roles: undefined,
    };
  }
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const roles: unknown = payload.scope_roles;
    // Tokens sealed before the claim became a list carry the singular form.
    const folderIds: unknown =
      payload.scope_flow_folder_ids ??
      (payload.scope_flow_folder_id
        ? [payload.scope_flow_folder_id]
        : undefined);
    return {
      orgId: payload.scope_org_id ?? undefined,
      wsId: payload.scope_ws_id ?? undefined,
      flowFolderIds:
        Array.isArray(folderIds) && folderIds.length > 0
          ? (folderIds as string[])
          : undefined,
      roles:
        Array.isArray(roles) && roles.length > 0
          ? (roles as string[])
          : undefined,
    };
  } catch {
    return {
      orgId: undefined,
      wsId: undefined,
      flowFolderIds: undefined,
      roles: undefined,
    };
  }
};

export type TokenRole = {
  name: string;
  arguments: Record<string, string>;
};

const ROLE_ARG_NAMES = ["org_id", "ws_id", "flow_folder_id"];

const parseRoleSignature = (signature: string): TokenRole => {
  const [name, argString] = signature.split("/");
  const values = argString ? argString.split(",") : [];
  return {
    name,
    arguments: Object.fromEntries(
      values
        .slice(0, ROLE_ARG_NAMES.length)
        .map((value, index) => [ROLE_ARG_NAMES[index], value]),
    ),
  };
};

export const decodeTokenRoles = (
  token: string | null | undefined,
): TokenRole[] | undefined => {
  if (!token) {
    return undefined;
  }
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const roles: unknown = payload.roles;
    if (!Array.isArray(roles)) {
      return undefined;
    }
    return roles
      .filter((signature): signature is string => typeof signature === "string")
      .map(parseRoleSignature);
  } catch {
    return undefined;
  }
};
